const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Formats event date from DD/MM/YYYY to "Weekday, D Month YYYY"
 * (e.g. "Friday, 8 May 2026"). Hand-formatted so the comma after the
 * weekday is guaranteed regardless of ICU build (`en-GB` toLocaleDateString
 * omits the comma in some Node versions).
 *
 * @param dateStr - Date string in DD/MM/YYYY format
 * @returns Formatted date string (e.g. "Friday, 8 May 2026")
 */
export function formatEventDate(dateStr: string): string {
  try {
    const [day, month, year] = dateStr.split('/');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (Number.isNaN(date.getTime())) return dateStr;
    return `${WEEKDAY_NAMES[date.getDay()]}, ${date.getDate()} ${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
  } catch (error) {
    return dateStr;
  }
}

/**
 * Combine SE365 DD/MM/YYYY date + HH:MM (24h) time into the standardised
 * event-card display:  `Friday, 8 May 2026 – 21:00`. Separator is the
 * en-dash (U+2013). If `timeStr` is empty/missing, the trailing
 * "– HH:MM" is omitted. Used by event cards, the event detail page,
 * the checkout page (OrderSummary), and the booking pages so the same
 * formatting appears everywhere SE365 dates surface.
 */
export function formatEventDateTime(dateStr: string, timeStr?: string): string {
  try {
    const datePart = formatEventDate(dateStr);
    const t = (timeStr || '').trim();
    return t ? `${datePart} – ${t}` : datePart;
  } catch {
    return [dateStr, timeStr].filter(Boolean).join(' ');
  }
}

/**
 * Formats event date to short format
 * @param dateStr - Date string in DD/MM/YYYY format
 * @returns Short formatted date string (e.g., "Nov 25, 2023")
 */
export function formatEventDateShort(dateStr: string): string {
  try {
    const [day, month, year] = dateStr.split('/');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    return dateStr;
  }
}

/**
 * Formats time from 24-hour to 12-hour format
 * @param timeStr - Time string in HH:MM format
 * @returns Formatted time string (e.g., "12:30 PM")
 */
export function formatEventTime(timeStr: string): string {
  try {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    
    return `${displayHour}:${minutes} ${period}`;
  } catch (error) {
    return timeStr;
  }
}

/**
 * Calculates time remaining until hold expires
 * @param expiresAt - ISO date string or Date object
 * @returns Object with hours, minutes, seconds remaining
 */
export function getTimeRemaining(expiresAt: string | Date): {
  total: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
} {
  const expiry = typeof expiresAt === 'string' ? new Date(expiresAt) : expiresAt;
  const total = expiry.getTime() - Date.now();
  
  if (total <= 0) {
    return { total: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }
  
  return {
    total,
    hours: Math.floor(total / (1000 * 60 * 60)),
    minutes: Math.floor((total / (1000 * 60)) % 60),
    seconds: Math.floor((total / 1000) % 60),
    expired: false,
  };
}

/**
 * Formats time remaining as a readable string
 * @param expiresAt - ISO date string or Date object
 * @returns Formatted string (e.g., "23h 45m 30s")
 */
export function formatTimeRemaining(expiresAt: string | Date): string {
  const { hours, minutes, seconds, expired } = getTimeRemaining(expiresAt);
  
  if (expired) {
    return 'Expired';
  }
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  
  return `${seconds}s`;
}
